import type {
  ActionGroupStatus,
  Job,
  OutputData,
  OutputReference,
} from "@smartsuite/automation-engine-api";
import { findActionConfigByInstanceId } from "./config-walk";

type ActionStatusItem = Extract<
  ActionGroupStatus["status"],
  { oneofKind: "actions" }
>["actions"]["actions"][number];

/**
 * Recursively collects all action outputs from a status tree.
 * Single point of truth for the status-tree shape: the Conditional Logic
 * feature adds its "branches" arm here and every consumer inherits it.
 *
 * `loopRecordIndex` narrows every loop group to the single iteration with
 * that index, so a reference evaluated inside iteration N resolves to that
 * iteration's outputs instead of always the first match (= iteration 0).
 * The same index is applied to every loop level; per-level indexes for
 * nested loops are not distinguishable in the UI yet.
 */
export const collectActionOutputs = (
  groups: ActionGroupStatus[],
  loopRecordIndex?: number,
): ActionStatusItem[] =>
  groups.flatMap((group) => {
    if (group.status.oneofKind === "actions") {
      return group.status.actions.actions;
    }
    if (group.status.oneofKind === "loop") {
      const recordActions =
        loopRecordIndex === undefined
          ? group.status.loop.recordActions
          : group.status.loop.recordActions.filter(
              (record) => record.index === loopRecordIndex,
            );
      return recordActions.flatMap((record) =>
        collectActionOutputs(record.actionGroups, loopRecordIndex),
      );
    }
    if (group.status.oneofKind === "branches") {
      const { branches, elseBranch } = group.status.branches;
      return [
        ...branches.flatMap((branch) =>
          collectActionOutputs(branch.actionGroups, loopRecordIndex),
        ),
        ...(elseBranch !== undefined
          ? collectActionOutputs(elseBranch.actionGroups, loopRecordIndex)
          : []),
      ];
    }
    if (group.status.oneofKind === "repeat") {
      return group.status.repeat.steps.flatMap((step) =>
        collectActionOutputs(step.actionGroups, loopRecordIndex),
      );
    }
    return [];
  });

// Internal to this module now (resolveOutputReference below): the record-list
// current-item source reads records from lazily-fetched RecordJobData, not
// from inline status, so get-current-item no longer needs it.
const findActionOutput = (
  groups: ActionGroupStatus[],
  instanceId: number,
  loopRecordIndex?: number,
): ActionStatusItem | undefined =>
  collectActionOutputs(groups, loopRecordIndex).find(
    (action) => action.actionReference?.instanceId === instanceId,
  );

/**
 * instanceId of the "actions" group that directly contains the action with
 * `actionInstanceId`, recursing through loop/branches/repeat blocks. This is
 * the `group-{id}` segment of that action's record-job path (getRecordJobId):
 * a find action feeding a loop is normally a top-level sibling (group 0), but
 * the walk also handles a source action nested inside another block.
 */
export const findActionsGroupInstanceId = (
  groups: ActionGroupStatus[],
  actionInstanceId: number,
): number | undefined => {
  for (const group of groups) {
    if (group.status.oneofKind === "actions") {
      if (
        group.status.actions.actions.some(
          (action) => action.actionReference?.instanceId === actionInstanceId,
        )
      ) {
        return group.instanceId;
      }
    } else if (group.status.oneofKind === "loop") {
      for (const record of group.status.loop.recordActions) {
        const found = findActionsGroupInstanceId(
          record.actionGroups,
          actionInstanceId,
        );
        if (found !== undefined) return found;
      }
    } else if (group.status.oneofKind === "branches") {
      const { branches, elseBranch } = group.status.branches;
      for (const branch of branches) {
        const found = findActionsGroupInstanceId(
          branch.actionGroups,
          actionInstanceId,
        );
        if (found !== undefined) return found;
      }
      if (elseBranch !== undefined) {
        const found = findActionsGroupInstanceId(
          elseBranch.actionGroups,
          actionInstanceId,
        );
        if (found !== undefined) return found;
      }
    } else if (group.status.oneofKind === "repeat") {
      for (const step of group.status.repeat.steps) {
        const found = findActionsGroupInstanceId(
          step.actionGroups,
          actionInstanceId,
        );
        if (found !== undefined) return found;
      }
    }
  }
  return undefined;
};

/**
 * A record trigger (created/updated) carries the referenced field outputs on
 * its RECORD -- `trigger.recordList.recordList.records[0].outputs` -- while
 * its top-level `outputs` array is EMPTY (hotfix job ff01253b: Loop by Field
 * over Email, `outputs: []`, `field-sd955bd263` on the record). Non-record
 * triggers (webhook) keep theirs top-level, so both places are searched,
 * top-level first (STR-14148).
 */
const triggerOutputs = (job: Job): OutputData[] => [
  ...(job.trigger?.outputs ?? []),
  ...(job.trigger?.recordList?.recordList?.records[0]?.outputs ?? []),
];

/**
 * Resolves an OutputReference to the OutputData it points at within a job.
 * A loopItem (Current Item) source has no stored output in a Job and any
 * missing output is unresolvable -- both return undefined so render paths
 * can degrade instead of throwing.
 *
 * An action's outputs are read from its status' own `outputs` only. A RECORD
 * action leaves that array empty and carries its fields on its record job
 * instead (the status keeps just `recordListCount`), so those references are
 * resolved by the caller against lazily fetched RecordJobData -- see
 * get-current-item.ts's actionOutputValueFromRecordJob. Mirroring the trigger
 * fallback here would not help: `recordList` is absent from an action's status
 * in the JobDetails payload.
 */
export const resolveOutputReference = (
  reference: OutputReference,
  job: Job,
  loopRecordIndex?: number,
): OutputData | undefined => {
  const outputs =
    reference.source.oneofKind === "triggerReference"
      ? triggerOutputs(job)
      : reference.source.oneofKind === "actionReference"
        ? (findActionOutput(
            job.actionGroupsStatus,
            reference.source.actionReference.instanceId,
            loopRecordIndex,
          )?.outputs ?? [])
        : [];

  return outputs.find((output) => output.outputId === reference.outputId);
};

export const getRecordJobId = (
  jobId: string,
  groupInstanceId: number,
  actionInstanceId: number | undefined,
  recordIndex: number,
): string =>
  `jobs/${jobId}/group-${groupInstanceId}/action-${String(
    actionInstanceId,
  )}/record-${recordIndex}`;

/**
 * Record-job id of an action whose record jobs the engine created FROM a
 * parent record job -- the path carries `parent-record-index-{parent's own
 * record index}` plus the action's own record index. Two shapes do:
 *
 *   - an action in a BRANCH inside a loop iteration: parent = the iteration's
 *     record job, own index 0 (the default) -- dev job 0bda3708 (loop ->
 *     branches -> create-record: only `parent-record-index-{iteration}/
 *     record-0` rows exist, no plain ones);
 *   - an action in a NESTED loop (loop in loop): parent = the OUTER
 *     iteration's record job, own index = the INNER iteration -- hotfix job
 *     3d602f47 (loop -> loop -> create-record, STR-14152: exactly
 *     `parent-record-index-{0,1}/record-{0,1}`, no plain rows).
 *
 * NOT for actions DIRECTLY in a top-level loop's actions group: those get one
 * PLAIN `record-{iterationIndex}` row per iteration (getRecordJobId) -- hotfix
 * jobs fa133404/637c3b54 (current-item actions, STR-14248) and dev job
 * 9bf92e4c (both the in-loop find, BUG-21299, and the update-after-find,
 * whose plain rows are coordinators carrying recordJobCreatedCount; only its
 * CHILDREN live at the parent path -- WithFindWrapper's contract). Matches
 * the engine: createRecordJobPath adds the parent segment only when a record
 * job is created FROM a record job. NestedGroupsRenderer owns the
 * distinction.
 *
 * DEEPER NESTING: the engine composes a child's parent segment from the
 * parent record job's own chain plus its record index (one dash-joined
 * segment, e.g. children of coordinator parent-record-index-1/record-0 live
 * at parent-record-index-1-0/record-{n} -- hotfix job 78f7e1ef, STR-13906;
 * WithFindWrapper's getParentRecordIndex owns that derivation). Shapes
 * beyond these two (branch inside a nested loop, three loop levels) have no
 * verified payload yet -- extend from evidence, not from this comment.
 */
export const getLoopNestedRecordJobId = (
  jobId: string,
  groupInstanceId: number,
  actionInstanceId: number | undefined,
  parentRecordIndex: number,
  recordIndex = 0,
): string =>
  `jobs/${jobId}/group-${groupInstanceId}/action-${String(
    actionInstanceId,
  )}/parent-record-index-${parentRecordIndex}/record-${recordIndex}`;

/**
 * How many record jobs an action fanned out into, or undefined when the job
 * does not say. This is the count to trust wherever record-job IDS are built
 * from it, because it comes from what the engine recorded rather than from the
 * grouped copy the renderers walk -- that copy's `recordListCount` means a
 * different thing per action kind. Verified against dev job ac5f878b, a branch
 * running Find records -> Update Records over 8 records:
 *
 *   flat    update-record -> recordListState.recordFinishedCount = 8
 *   grouped update-record -> recordListCount = 1  (the record-list job)
 *   grouped find-records  -> recordListCount = 8  (records FOUND, which all
 *                                                  live in its record-0 job)
 *
 * so reading the grouped copy would under-report the update and invent
 * record-1..7 ids for the find, which the engine 404s.
 *
 * Two sources, in order: the action's own progress state, and -- for a failed
 * run, which has none -- the size of the record list it processed. See each
 * helper at the bottom of this module.
 */
export const getActionRecordJobCount = (
  action: ActionStatusItem,
  job?: Job,
): number | undefined =>
  getFlatProgressCount(action, job) ?? getRecordListSourceCount(action, job);

/**
 * How many record jobs an action produced. Single point of truth so every
 * action renderer treats the count the same way.
 *
 * The engine's own count above comes first: reading the grouped copy the
 * renderers walk rendered a single "Record 1" for an Update Records over 8
 * records (BUG-21290). The grouped fields stay as fallbacks for actions the
 * flat list does not describe, and single-record actions (e.g. Create a
 * comment) carry neither field but still have exactly one record-0 job to
 * show -- hence the final 1.
 */
export const getActionRecordCount = (
  action: ActionStatusItem,
  job?: Job,
): number =>
  getActionRecordJobCount(action, job) ??
  action.recordListState?.recordFinishedCount ??
  action.recordListCount ??
  1;

/**
 * The action's own progress state, from the job's FLAT `actions` list (Job
 * field 5) rather than the grouped copy in `actionGroupsStatus` (field 13).
 */
const getFlatProgressCount = (
  action: ActionStatusItem,
  job?: Job,
): number | undefined => {
  const instanceId = action.actionReference?.instanceId;
  // `actions` is typed non-optional but proto3 JSON omits empty repeated
  // fields, so it really can be absent on a job with no action data.
  const flat =
    instanceId === undefined
      ? undefined
      : job?.actions?.find(
          (candidate) => candidate.actionReference?.instanceId === instanceId,
        );

  return flat?.recordListState?.recordFinishedCount;
};

/**
 * Size of the record LIST this action processed, read from the action that
 * produced that list -- the only count a FAILED job still carries.
 *
 * The engine leaves the flat `actions` list EMPTY when a run ends in
 * ACTION_FAIL. Two runs of the same dev automation, one action apart:
 *
 *   716cb5a3 (ok)                    -> actions[update].recordListState
 *                                       .recordFinishedCount = 8
 *   acae98ad (its send-email failed) -> actions: []
 *
 * while acae98ad's eight update record jobs (group-0/action-2/record-0..7) all
 * exist. So one failing LATER action collapsed an Update Records over 8 records
 * to a single "Record 1" (BUG-21303), since the grouped copy the renderers walk
 * only ever says recordListCount = 1.
 *
 * The config's `recordList` names the action whose records this one processed,
 * and that action's own count survives in the failed job's status
 * (recordListCount = 8 on the find) -- one record job per record in the list.
 *
 * Only for an action that itself finished ok: one that failed part-way through
 * its list created FEWER record jobs than the list holds, and counting the list
 * would render rows with nothing behind them.
 */
const getRecordListSourceCount = (
  action: ActionStatusItem,
  job?: Job,
): number | undefined => {
  const instanceId = action.actionReference?.instanceId;
  if (instanceId === undefined || action.status.oneofKind !== "ok" || !job) {
    return undefined;
  }

  const source = findActionConfigByInstanceId(
    job.automation?.actionGroups ?? [],
    instanceId,
  )?.recordList?.recordList;
  if (source?.oneofKind !== "actionReference") {
    return undefined;
  }

  const sourceInstanceId = source.actionReference.instanceId;

  return collectActionOutputs(job.actionGroupsStatus ?? []).find(
    (candidate) => candidate.actionReference?.instanceId === sourceInstanceId,
  )?.recordListCount;
};
